import { useVoice } from '@/stores/realtime';

/**
 * UI sounds. The files live in /sounds (generated or replaced by
 * `npm run brand`); /sounds/sounds.json says which file each sound uses.
 * Sounds are fetched on first use and cached as decoded audio.
 */

export type SoundName =
  | 'message'
  | 'mention'
  | 'join'
  | 'leave'
  | 'user-join'
  | 'user-leave'
  | 'mute'
  | 'unmute'
  | 'deafen'
  | 'undeafen'
  | 'stream-start'
  | 'stream-end';

let ctx: AudioContext | null = null;
let manifest: Promise<Record<string, string>> | null = null;
const buffers = new Map<SoundName, Promise<AudioBuffer | null>>();

function loadManifest() {
  manifest ??= fetch('/sounds/sounds.json')
    .then((r) => (r.ok ? (r.json() as Promise<Record<string, string>>) : {}))
    .catch(() => ({}));
  return manifest;
}

function load(name: SoundName): Promise<AudioBuffer | null> {
  let pending = buffers.get(name);
  if (!pending) {
    pending = loadManifest()
      .then((files) => fetch(files[name] ?? `/sounds/${name}.wav`))
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error('missing sound'))))
      .then((data) => ctx!.decodeAudioData(data))
      .catch(() => null);
    buffers.set(name, pending);
  }
  return pending;
}

/**
 * Plays a UI sound. Silently does nothing if audio isn't available, and
 * never plays while you're deafened (you asked for silence).
 */
export function playSound(name: SoundName, volume = 0.45, opts: { evenIfDeafened?: boolean } = {}) {
  if (!opts.evenIfDeafened && useVoice.getState().deafened) return;
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    const context = ctx;
    void load(name).then((buffer) => {
      if (!buffer) return;
      const source = context.createBufferSource();
      const gain = context.createGain();
      gain.gain.value = volume;
      source.buffer = buffer;
      source.connect(gain).connect(context.destination);
      source.start();
    });
  } catch {
    /* Audio unavailable. */
  }
}
